import { useEffect, useState } from "react";
import { AlertTriangle, RefreshCw, Save } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import {
  previewRetention,
  saveTelephonySettings,
  type RetentionDue,
  type TelephonyStatus,
} from "../../../../lib/calls";
import { Btn, StatusPill } from "../../../ui";
import { ConfirmModal } from "../../_shared/ConfirmModal";
import { Field, FieldGroup, Note, Pill, SaveRow, ToggleRow } from "../panel/fields";

/**
 * Mirrors RETENTION_DAYS_MIN / _MAX in the API's crm-calls config.
 *
 * Duplicated on purpose and only for the message: the server clamps whatever it
 * is handed regardless, so these two never decide what is stored - they decide
 * what this form refuses to send, and what the sentence under it says.
 */
const DAYS_MIN = 7;
const DAYS_MAX = 3650;

/** A day count the form is willing to post: a whole number inside the bounds. */
function inBounds(value: string): boolean {
  const n = Number(value);
  return Number.isInteger(n) && n >= DAYS_MIN && n <= DAYS_MAX;
}

/**
 * Settings -> Recording storage: the schedule that deletes old calls.
 *
 * Its own file rather than more lines on RecordingStorageCard, because it is a
 * different question. That card is about copies Twilio is still billing us for
 * and cleaning them up on demand; this is how long a conversation may be kept at
 * all, which is the answer to somebody asking why we still have their voice.
 *
 * Three things it does that a plain settings form does not:
 *
 *  - It shows the count BEFORE the switch, and for the ages ON SCREEN rather than
 *    the ages already stored. The distinction is the whole value of the number:
 *    somebody who types 7 over 365 and reads a count taken at 365 has been shown
 *    a reassuring zero on the way to deleting the archive. `retentionDue` off the
 *    settings response seeds first paint; every edit after that re-asks
 *    `/retention/preview`, and the count is marked stale until the answer lands.
 *  - Deleting MORE is confirmed - switching on, and also shortening either age
 *    while it already runs. Every other switch on this page changes what happens
 *    next; this one reaches backwards into the archive.
 *  - A count the server could not produce renders as "not known", never as zero,
 *    in the dialog as well as in the note. Zero is the one wrong answer that
 *    reads as permission.
 */
export function RetentionFields({
  status,
  onSaved,
}: {
  /** The loaded settings, or null while the card is still fetching them. */
  status: TelephonyStatus | null;
  /** Hand the PUT's answer back up, so the card and this block stay in step. */
  onSaved: (next: TelephonyStatus) => void;
}) {
  const t = useT();
  const [enabled, setEnabled] = useState(false);
  // Strings, like every other numeric field in this section: a number in state
  // makes the input impossible to clear while typing.
  const [recordingDays, setRecordingDays] = useState("90");
  const [transcriptDays, setTranscriptDays] = useState("365");
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error" | "bounds">("idle");
  const [confirming, setConfirming] = useState(false);

  // The count for the ages currently typed. `undefined` = still being fetched,
  // `null` = the server could not produce one. The two are different sentences.
  const [due, setDue] = useState<RetentionDue | null | undefined>(undefined);

  useEffect(() => {
    if (!status) return;
    setEnabled(status.retention.enabled);
    setRecordingDays(String(status.retention.recordingDays));
    setTranscriptDays(String(status.retention.transcriptDays));
    // First paint reuses the count the settings response already carried, which
    // is correct precisely because the fields were just seeded from the same
    // stored ages. It stops being correct on the first keystroke, which is what
    // the effect below is for.
    setDue(status.retentionDue);
  }, [status]);

  // Re-ask whenever either age changes. Debounced because it fires per keystroke
  // and each answer is two counts over the whole call log; `cancelled` is what
  // stops a slow early reply from overwriting a fast later one.
  useEffect(() => {
    if (!inBounds(recordingDays) || !inBounds(transcriptDays)) return;
    if (
      status &&
      Number(recordingDays) === status.retention.recordingDays &&
      Number(transcriptDays) === status.retention.transcriptDays
    ) {
      return; // Unedited: the seeded count already answers these exact ages.
    }
    let cancelled = false;
    setDue(undefined);
    const timer = setTimeout(() => {
      previewRetention({ recordingDays: Number(recordingDays), transcriptDays: Number(transcriptDays) })
        .then((next) => { if (!cancelled) setDue(next); })
        .catch(() => { if (!cancelled) setDue(null); });
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [recordingDays, transcriptDays, status]);

  async function persist(next: boolean) {
    setSaveState("saving");
    try {
      onSaved(
        await saveTelephonySettings({
          retention: {
            enabled: next,
            recordingDays: Number(recordingDays),
            transcriptDays: Number(transcriptDays),
          },
        }),
      );
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 2500);
    } catch {
      setSaveState("error");
    } finally {
      setConfirming(false);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Checked here rather than left to the server's clamp, which is the wrong
    // shape of help for this particular field: an emptied box reads as 0, and
    // silently turning that into the seven-day floor would delete almost the
    // whole archive on behalf of somebody who was mid-edit. Say what is wrong
    // and save nothing. The server clamps anyway, for rows this form never saw.
    if (!inBounds(recordingDays) || !inBounds(transcriptDays)) {
      setSaveState("bounds");
      return;
    }
    // Asked whenever the save would delete MORE than the stored policy does:
    // switching on, and shortening either age while it already runs. Not asked
    // for switching off or lengthening a window, because neither can remove
    // anything - and a dialog on every edit is a dialog people learn to click
    // through, which would cost the two that matter.
    if (enabled && widensDeletion(status)) setConfirming(true);
    else void persist(enabled);
  }

  /** Would saving this form take calls the stored policy currently keeps? */
  function widensDeletion(saved: TelephonyStatus | null): boolean {
    if (!saved?.retention.enabled) return true; // Off -> on: everything past the line is new.
    return (
      Number(recordingDays) < saved.retention.recordingDays ||
      Number(transcriptDays) < saved.retention.transcriptDays
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <FieldGroup title={t("settings.recordingStorage.retentionGroup")}>
        <ToggleRow
          label={t("settings.recordingStorage.retention")}
          hint={t("settings.recordingStorage.retentionHint")}
          checked={enabled}
          onChange={setEnabled}
        />
      </FieldGroup>

      <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
        <Field
          label={t("settings.recordingStorage.recordingDays")}
          hint={t("settings.recordingStorage.recordingDaysHint")}
        >
          <Pill
            className="h-10 max-w-[120px]"
            value={recordingDays}
            onChange={(v) => { setRecordingDays(v); setSaveState("idle"); }}
            inputMode="numeric"
            placeholder="90"
          />
        </Field>
        <Field
          label={t("settings.recordingStorage.transcriptDays")}
          hint={t("settings.recordingStorage.transcriptDaysHint")}
        >
          <Pill
            className="h-10 max-w-[120px]"
            value={transcriptDays}
            onChange={(v) => { setTranscriptDays(v); setSaveState("idle"); }}
            inputMode="numeric"
            placeholder="365"
          />
        </Field>
      </div>

      {/* The count the operator is really deciding about. Four shapes, and the
          two that are not a number matter most: "still counting" must not look
          like "nothing due", and "could not count" must not look like zero. */}
      {due === undefined ? (
        <Note tone="mut">{t("settings.recordingStorage.dueCounting")}</Note>
      ) : due === null ? (
        <Note tone="mut">{t("settings.recordingStorage.dueUnknown")}</Note>
      ) : due.recordings + due.transcripts === 0 ? (
        <Note tone="ok">{t("settings.recordingStorage.dueNone")}</Note>
      ) : (
        <Note
          tone={status?.retention.enabled ? "info" : "bad"}
          icon={
            status?.retention.enabled ? undefined : (
              <AlertTriangle className="mt-0.5 size-4 shrink-0" />
            )
          }
        >
          {t("settings.recordingStorage.dueRecordings", { n: due.recordings })}
          {" "}
          {t("settings.recordingStorage.dueTranscripts", { n: due.transcripts })}
        </Note>
      )}

      {saveState === "error" ? (
        <Note tone="bad" live>{t("settings.recordingStorage.retentionSaveFailed")}</Note>
      ) : null}
      {saveState === "bounds" ? (
        <Note tone="bad" live>
          {t("settings.recordingStorage.retentionBounds", { min: DAYS_MIN, max: DAYS_MAX })}
        </Note>
      ) : null}

      <SaveRow>
        <Btn type="submit" tone="dark" disabled={saveState === "saving" || !status}>
          {saveState === "saving" ? <RefreshCw className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
          {saveState === "saving"
            ? t("settings.recordingStorage.retentionSaving")
            : t("settings.recordingStorage.retentionSave")}
        </Btn>
        {saveState === "saved" ? (
          <StatusPill tone="ok">{t("settings.recordingStorage.retentionSaved")}</StatusPill>
        ) : null}
      </SaveRow>

      {confirming ? (
        <ConfirmModal
          title={t("settings.recordingStorage.confirmRetentionTitle")}
          // Two bodies, and which one appears depends on whether there is a
          // count to state. Printing "0 calls" when the count is unknown or
          // still running would put the single most reassuring number in the
          // product in front of the single most irreversible button.
          body={
            due
              ? t("settings.recordingStorage.confirmRetentionBody", {
                  recordings: due.recordings,
                  transcripts: due.transcripts,
                  days: Number(recordingDays),
                })
              : t("settings.recordingStorage.confirmRetentionBodyUnknown", {
                  days: Number(recordingDays),
                })
          }
          confirmLabel={t("settings.recordingStorage.confirmRetentionYes")}
          cancelLabel={t("common.cancel")}
          onConfirm={() => persist(true)}
          onClose={() => setConfirming(false)}
        />
      ) : null}
    </form>
  );
}
